import { createVoiceTiming, observeVoicePaintOpportunity } from '@/lib/voice-timing';
import { useState, useCallback, useRef, useEffect } from 'react';
import i18n from '@/i18n';
import {
  getEffectiveEnhancedVoice,
  useSettingsStore,
} from '@/lib/settings-store';
import { extFromMimeType, isTemporaryTranscriptionServiceFailure } from '@/lib/grok';
import {
  dispatchVoiceStartUnavailable,
  voiceStartUnavailableReason,
} from '@/lib/voice-start-unavailable-events';
import {
  CAPTURE_RETIRED_ERROR,
  persistInlineTranscription,
  persistUploadedAudioPointer,
  runInlineAudioExclusive,
  transcribeRegisteredQueueAudio,
  uploadAudioToStorage,
} from '@/lib/audio-queue';
import { getSupabase } from '@/lib/supabase';
import { pulseFirstVoice, pulseStart, pulseStop } from '@/lib/haptics';
import { dispatchVoiceAutoStop } from '@/lib/voice-auto-stop-events';
import { enhancedVoiceAllowed, isCoreReadOnly } from '@/lib/core-cutover';
import { getContentOwnerBinding } from '@/lib/content-owner';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import { getAudioDeviceId } from '@/lib/audio-registry';
import type { AudioRecoveryDestination } from '@/types/audio-recovery';
import type { ScriptureAnchor } from '@/types/prayer';
import { cloneScriptureAnchor } from '@/lib/scripture-anchor';
import {
  admitCapture,
  awaitCapturePayload,
  awaitCaptureStopResult,
  captureMayContinue,
  captureMayStartRecording,
  claimCaptureStop,
  commitCapture,
  deliverRecorderStop,
  endCaptureWithoutPayload,
  finishCapturePipeline,
  markCaptureFailed,
  markCaptureRecording,
  retireCapture,
  revokeCaptureAdmission,
} from '@/lib/voice-capture-custody';

/**
 * V.A.3: silent 10-minute auto-stop cap on enhanced-mode recordings.
 * Bounds xAI processing time + Storage / function-memory cost. Browser
 * fallback path is uncapped — Web Speech API has its own silence-driven
 * auto-stop on Chrome and the cap reasons don't apply to it.
 */
const AUTO_STOP_MS = 10 * 60 * 1000;

/**
 * Maps the user's current language to a BCP-47 tag for the Web Speech
 * API's SpeechRecognition.lang property. Defaults to Latin American
 * Spanish and Brazilian Portuguese as the larger audiences. Only
 * affects the browser-fallback voice path; Enhanced Voice routes
 * through xAI STT which auto-detects language across 25+ locales
 * (see core rule #14 for the dual-path architecture). Change
 * deliberately, not casually — a future per-user dialect preference
 * in Settings is the right escape hatch if es-ES or pt-PT users
 * complain about accuracy.
 */
const SPEECH_RECOGNITION_LANG: Record<string, string> = {
  en: 'en-US',
  es: 'es-MX',
  pt: 'pt-BR',
};

interface SpeechOptions {
  onInterim?: (text: string) => void;
  onFinal: (text: string, queueItemId?: string) => void;
  /**
   * C2 (testimony plan rev 4, section 8.5): called once per enhanced capture
   * right after its owner-bound row is durable, before upload. Scheduled as a
   * microtask, never awaited and guarded, so it cannot change the pipeline.
   */
  onCaptureDurable?: (notice: CaptureDurableNotice) => void;
  /** Called once when an accepted session reaches any terminal state. */
  onSessionSettled?: (sessionSeq: number, outcome: SpeechSessionOutcome) => void;
  /**
   * The voice provider instance that admits captures (account-change
   * recording plan rev 4.1, section 3.1). Every capture is a custody record
   * from admission; the provider's teardown retires its records.
   */
  custodyRuntimeId?: string;
}

export type VoiceStopResult =
  | { durability: 'not-required' | 'committed' }
  | { durability: 'failed'; error: Error };

/** Read only from the recorder's own bindings at the durability boundary. */
export interface CaptureDurableNotice {
  queueId: string;
  ownerId: string;
  authEpoch: number;
  recoveryDestination?: AudioRecoveryDestination;
  captureToken?: string;
}

/** How an accepted session ended: a stop, a start that never recorded, an
 * ended browser session, or an interruption finalizer taking the capture. */
export type SpeechSessionOutcome =
  | { kind: 'stopped'; result: VoiceStopResult }
  | { kind: 'start-failed' }
  | { kind: 'ended' }
  | { kind: 'interrupted' };

/** Synchronous capture activity, read from refs (never React state). */
export interface SpeechCaptureActivity {
  /** Something would make a new start unsafe: starting, recording, a stop or
   * an interruption finalization still settling. */
  busy: boolean;
  /** A capture is recording and no stop or finalization has begun. */
  listening: boolean;
  /** A stop or interruption finalization has begun and not settled. */
  stopping: boolean;
}

export async function commitStoppedEnhancedCaptureBeforeNetwork(
  enqueue: () => Promise<unknown>,
  ownerStillCurrent: () => Promise<boolean>,
): Promise<
  | { status: 'durable'; ownerStillCurrent: boolean }
  | { status: 'failed'; error: Error }
> {
  try {
    await enqueue();
  } catch (error) {
    return {
      status: 'failed',
      error: error instanceof Error ? error : new Error('voice-durable-capture-failed'),
    };
  }

  try {
    return { status: 'durable', ownerStillCurrent: await ownerStillCurrent() };
  } catch {
    // Once the row is durable, a failed owner/session probe is a reason to
    // skip network work, never a reason to delete or downgrade the row.
    return { status: 'durable', ownerStillCurrent: false };
  }
}

export function assertVoiceStopDurable(result: VoiceStopResult): void {
  if (result.durability === 'failed') throw result.error;
}

/** The language recorded with a capture; frozen at admission. */
function captureLanguage(): string {
  const lang = (i18n.language || 'en').slice(0, 2);
  return lang === 'es' || lang === 'pt' ? lang : 'en';
}

export interface SpeechStartOptions {
  inlineSaveSource?: 'prayer' | 'bookmark';
  recoveryDestination?: AudioRecoveryDestination;
  /** Frozen passage provenance for Bible-surface recovery. */
  scriptureAnchor?: ScriptureAnchor;
  /** Opaque identity a caller mints per capture; bound to the enhanced
   * recorder and echoed in its durable notification (section 8.5). */
  captureToken?: string;
}

interface SpeechState {
  isListening: boolean;
  isTranscribing: boolean;
  isSupported: boolean;
  /** Read-only: a stop or interruption finalization is underway (section 8.4). */
  isStopping: boolean;
  error: string | null;
  /** Clears the latest error — used by the error toast when auto-dismissing
   *  or when the user taps it. Also called implicitly on every new session. */
  clearError: () => void;
  /** Returns the accepted session's sequence number, or null when refused. A
   * refusal changes nothing. */
  startListening: (opts?: SpeechStartOptions) => number | null;
  getCaptureActivity: () => SpeechCaptureActivity;
  stopListening: () => Promise<VoiceStopResult>;
  cancelListening: () => Promise<VoiceStopResult>;
  /**
   * Returns the current normalized audio level (0..1) in enhanced mode,
   * or null when no analyser is wired (browser STT mode, idle, or pre-
   * data window after recorder start). AudioMeter polls this per frame
   * to drive bar heights without triggering React re-renders.
   */
  getLevel: () => number | null;
  /**
   * Defensive lifecycle finalizer (B.2). Called by the visibility/
   * pagehide handler when the page is about to suspend. Synchronously
   * tears down the active session and returns the captured audio for
   * enqueuing — never attempts MediaRecorder resume across iOS blur.
   *
   * Returns:
   *   - { mode: 'enhanced', blob, mimeType } — caller enqueues to IDB
   *   - { mode: 'browser' } — caller persists interim text from its
   *     own state (useSpeechToText doesn't own the interim string)
   *   - null — no active session, or already finalized by a prior
   *     visibility event (idempotent per R3)
   *
   * NEVER pulses pulseStop — system-triggered finalization is not a
   * user action, so the haptic + visual cue would be misleading.
   */
  finalizeForInterruption: () => Promise<
    | {
        mode: 'enhanced';
        blob: Blob;
        mimeType: string;
        ownerId: string;
        authEpoch: number;
        queueId: string;
        destinationId: string;
        sourceDeviceId: string;
      }
    | {
        mode: 'browser';
        ownerId?: string;
        authEpoch?: number;
        queueId?: string;
        destinationId?: string;
        sourceDeviceId?: string;
      }
    | null
  >;
}

interface VoiceSessionIdentity {
  queueId: string;
  destinationId: string;
  sourceDeviceId: string;
  authEpoch: number;
  ownerId: string | null | undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RecognitionInstance = any;

function getSpeechRecognitionCtor(): (new () => RecognitionInstance) | undefined {
  if (typeof window === 'undefined') return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? undefined;
}

function hasMediaRecorder(): boolean {
  return typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined';
}

/**
 * A tap that could start no transcription (no Enhanced path for it and no
 * browser engine, or the browser engine refused to start) is explained by the
 * global voice toast (rule #26), never left silent. Only says something; it
 * starts nothing and changes no capture state.
 */
function explainStartUnavailable(): void {
  dispatchVoiceStartUnavailable(
    voiceStartUnavailableReason(
      useSettingsStore.getState().fallbackReason,
      enhancedVoiceAllowed(),
    ),
  );
}

async function voiceOwnerStillCurrent(
  expectedOwnerId: string,
  expectedAuthEpoch: number,
): Promise<boolean> {
  try {
    const sb = getSupabase();
    if (!sb) return false;
    const { data } = await sb.auth.getSession();
    return data.session?.user.id === expectedOwnerId &&
      getAuthUserId() === expectedOwnerId &&
      getAuthEpoch() === expectedAuthEpoch &&
      await getContentOwnerBinding() === expectedOwnerId;
  } catch {
    return false;
  }
}

// Pick the first supported audio mimeType. Safari defaults to audio/mp4 while
// Chrome defaults to audio/webm;codecs=opus. Without an explicit choice the
// upload filename/extension on the server can mismatch the container, and xAI
// STT silently returns empty text. Cached so we don't probe on every start.
let cachedMimeType: string | null = null;
function pickAudioMimeType(): string {
  if (cachedMimeType !== null) return cachedMimeType;
  if (typeof MediaRecorder === 'undefined') {
    cachedMimeType = '';
    return cachedMimeType;
  }
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  for (const mime of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(mime)) {
        cachedMimeType = mime;
        return cachedMimeType;
      }
    } catch {
      // Some engines throw on unsupported strings; try the next candidate.
    }
  }
  cachedMimeType = '';
  return cachedMimeType;
}

/**
 * V.A.3: clear the per-recorder auto-stop timer attached in startEnhanced.
 * Called from every path that retires the recorder (manual stop,
 * visibility-driven finalize, unmount cleanup). Per rule #14, the timer
 * ID is bound to the recorder INSTANCE — not a shared ref — so a second
 * concurrent session (which startingRef should already prevent) cannot
 * stomp the first session's timer. Idempotent.
 */
function clearAutoStopTimer(recorder: MediaRecorder | null): void {
  if (!recorder) return;
  const t = (recorder as RecognitionInstance).__autoStopTimer;
  if (t != null) clearTimeout(t);
  (recorder as RecognitionInstance).__autoStopTimer = null;
}

export function useSpeechToText(options: SpeechOptions): SpeechState {
  const { onInterim, onFinal, onCaptureDurable, onSessionSettled } = options;
  const custodyRuntimeIdRef = useRef(options.custodyRuntimeId ?? 'unscoped-voice-runtime');

  const onInterimRef = useRef(onInterim);
  const onFinalRef = useRef(onFinal);
  const onCaptureDurableRef = useRef(onCaptureDurable);
  const onSessionSettledRef = useRef(onSessionSettled);
  // R4 (testimony plan rev 4, section 8.7): every accepted session is tracked
  // until it settles, whatever ends it, so a new capture can never start while
  // an earlier one is still recording, stopping or being finalized.
  const sessionSeqRef = useRef(0);
  const activeSessionRef = useRef<{ seq: number; settled: boolean } | null>(null);
  const stopUnderwayRef = useRef(false);
  const interruptionInFlightRef = useRef(false);
  const [isStopping, setIsStopping] = useState(false);
  const recognitionRef = useRef<RecognitionInstance | null>(null);
  const shouldBeListening = useRef(false);
  // True ONLY while startEnhanced is mid-flight (the getUserMedia await
  // window + recorder setup). Dedicated flag rather than reusing
  // shouldBeListening — this one is guaranteed to clear via try/finally so
  // the guard can never get stuck.
  const startingRef = useRef(false);

  // Enhanced-mode state. We also track the mode for the current session so
  // stopListening takes the branch that matches startListening.
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const sessionModeRef = useRef<'browser' | 'enhanced' | null>(null);
  const stopPromiseRef = useRef<Promise<VoiceStopResult> | null>(null);
  // Minted only after the start guards accept a user gesture and before the
  // first asynchronous boundary. Reused by both actual capture modes so an
  // A -> B -> A account transition cannot adopt late recorder work.
  const sessionIdentityRef = useRef<VoiceSessionIdentity | null>(null);
  // Set by cancelListening for the stop it issues: that stop claims the capture
  // as an explicit Cancel, the only path that discards bytes (section 3.3).
  // Custody never reads it; the claim does.
  const cancelRequestedRef = useRef(false);

  // ---- Audio meter (B.1) ----
  // AudioContext + AnalyserNode tap on the same MediaStream the recorder
  // consumes. Both are independent consumers; coexistence is safe. The
  // tick loop updates levelRef per animation frame and fires the first-
  // voice pulse once the input crosses a sustained threshold. None of
  // this affects recording — if any of the WebAudio plumbing fails,
  // setupAudioMeter logs and bails so recording continues without a
  // live meter (AudioMeter falls back to its synthetic CSS pulse).
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const levelRef = useRef<number | null>(null);
  const meterRafRef = useRef<number | null>(null);
  const firstVoiceFiredRef = useRef(false);
  const firstVoiceWindowStartRef = useRef<number | null>(null);
  // True once a session has been confirmed started (recorder.start() or
  // recognition.start() succeeded). Drives pulseStart/pulseStop gating
  // so browser-mode auto-restart inside recognition.onend doesn't
  // double-pulse mid-session.
  const sessionStartedRef = useRef(false);

  // V.A.3: ref bridge so the auto-stop setTimeout callback in
  // startEnhanced can route through the public stopListening() — which
  // is declared later in this hook (TS would reject a direct lexical
  // reference). Updated by the useEffect at the bottom of this hook.
  // The timer doesn't fire for 10 minutes, by which time the effect
  // has long since assigned the current stopListening; on first render
  // the ref is null but no timer has armed yet either.
  const stopListeningRef = useRef<(() => Promise<VoiceStopResult>) | null>(null);

  // ---- Defensive lifecycle (B.2) ----
  // hasInterruptionFinalized: idempotency guard per R3. Visibility/
  // pagehide can fire in rapid succession on iOS Safari (lock screen
  // → notification → return), and useSpeechToText's unmount cleanup
  // can fire concurrently. The first finalize wins; subsequent calls
  // no-op cleanly. Reset on next startListening.
  const hasInterruptionFinalizedRef = useRef(false);
  const [isListening, setIsListening] = useState(false);
  // True only in enhanced mode between "user tapped stop" and "transcription
  // returned/failed". Browser mode streams finals — there's no wait — so this
  // stays false.
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Browser mode is supported if SpeechRecognition exists. Enhanced mode is
  // supported only when the deployment gate is open and MediaRecorder +
  // getUserMedia exist. This keeps the mic from advertising an unavailable
  // cloud-only path on browsers without Web Speech.
  const isSupported =
    !!getSpeechRecognitionCtor() ||
    (enhancedVoiceAllowed() &&
      hasMediaRecorder() &&
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia);

  useEffect(() => {
    onInterimRef.current = onInterim;
  }, [onInterim]);

  useEffect(() => {
    onFinalRef.current = onFinal;
  }, [onFinal]);

  useEffect(() => {
    onCaptureDurableRef.current = onCaptureDurable;
  }, [onCaptureDurable]);

  useEffect(() => {
    onSessionSettledRef.current = onSessionSettled;
  }, [onSessionSettled]);

  const markStopUnderway = useCallback(() => {
    stopUnderwayRef.current = true;
    setIsStopping(true);
  }, []);

  // Settles the accepted session `seq` exactly once. The observer runs in a
  // microtask so it never fires inside the synchronous start that accepted the
  // session, and an observer failure cannot reach the capture paths.
  const settleSession = useCallback((seq: number, outcome: SpeechSessionOutcome) => {
    const active = activeSessionRef.current;
    if (!active || active.seq !== seq || active.settled) return;
    active.settled = true;
    stopUnderwayRef.current = false;
    setIsStopping(false);
    const observer = onSessionSettledRef.current;
    if (!observer) return;
    queueMicrotask(() => {
      try {
        observer(seq, outcome);
      } catch {
        // Observer only.
      }
    });
  }, []);

  const unsettledSessionSeq = useCallback((): number | null => {
    const active = activeSessionRef.current;
    return active && !active.settled ? active.seq : null;
  }, []);

  // --- Audio meter helpers (B.1) ---

  const teardownAudioMeter = useCallback(() => {
    if (meterRafRef.current != null) {
      cancelAnimationFrame(meterRafRef.current);
      meterRafRef.current = null;
    }
    const ctx = audioContextRef.current;
    if (ctx) {
      // close() returns a promise that rejects if already closed; we
      // don't care about either outcome past releasing the reference.
      ctx.close().catch(() => {});
    }
    audioContextRef.current = null;
    analyserRef.current = null;
    levelRef.current = null;
    firstVoiceFiredRef.current = false;
    firstVoiceWindowStartRef.current = null;
  }, []);

  const setupAudioMeter = useCallback(
    (stream: MediaStream): void => {
      // Best-effort. Any failure here is non-fatal — recording continues
      // without the live meter and AudioMeter falls back to its synthetic
      // CSS pulse, so the user still sees motion. Common failure modes:
      // older browsers without AudioContext, iOS Safari refusing to
      // create a context outside a gesture window, or Safari rejecting
      // createMediaStreamSource for certain MediaStream configurations.
      try {
        type AudioContextCtor = typeof AudioContext;
        const w = window as unknown as {
          AudioContext?: AudioContextCtor;
          webkitAudioContext?: AudioContextCtor;
        };
        const Ctx = w.AudioContext ?? w.webkitAudioContext;
        if (!Ctx) return;

        const ctx = new Ctx();
        audioContextRef.current = ctx;

        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        analyserRef.current = analyser;

        firstVoiceFiredRef.current = false;
        firstVoiceWindowStartRef.current = null;

        const freqData = new Uint8Array(analyser.frequencyBinCount);
        const timeData = new Uint8Array(analyser.fftSize);

        // First-voice trigger thresholds. Tunable per E.9 in the B.1
        // plan: if the manual gate finds whispered prayers don't trip
        // the haptic+flash, drop FIRST_VOICE_RMS to ~0.025 and reship
        // as B.1.1.
        const FIRST_VOICE_RMS = 0.04;
        const FIRST_VOICE_HOLD_MS = 100;

        const tick = () => {
          const a = analyserRef.current;
          if (!a) {
            meterRafRef.current = null;
            return;
          }

          // Frequency-domain mean drives the meter bars (perceived
          // volume across the full band). Normalized 0..1.
          a.getByteFrequencyData(freqData);
          let freqSum = 0;
          for (let i = 0; i < freqData.length; i++) freqSum += freqData[i];
          levelRef.current = freqSum / freqData.length / 255;

          // Time-domain RMS drives the first-voice trigger — more peak-
          // sensitive than mean energy, so a sharp word-onset trips
          // faster than the smoothed bar level would.
          if (!firstVoiceFiredRef.current) {
            a.getByteTimeDomainData(timeData);
            let sumSq = 0;
            for (let i = 0; i < timeData.length; i++) {
              const v = (timeData[i] - 128) / 128;
              sumSq += v * v;
            }
            const rms = Math.sqrt(sumSq / timeData.length);

            if (rms > FIRST_VOICE_RMS) {
              if (firstVoiceWindowStartRef.current == null) {
                firstVoiceWindowStartRef.current = performance.now();
              } else if (
                performance.now() - firstVoiceWindowStartRef.current >=
                FIRST_VOICE_HOLD_MS
              ) {
                firstVoiceFiredRef.current = true;
                pulseFirstVoice();
              }
            } else {
              firstVoiceWindowStartRef.current = null;
            }
          }

          meterRafRef.current = requestAnimationFrame(tick);
        };
        meterRafRef.current = requestAnimationFrame(tick);
      } catch (err) {
        console.warn('[Adia] Audio meter setup failed; falling back to synthetic pulse.', err);
        teardownAudioMeter();
      }
    },
    [teardownAudioMeter],
  );

  const getLevel = useCallback((): number | null => {
    // analyserRef is set only when the live tap is wired and producing
    // data. Browser STT mode, idle, and the brief gap between recorder
    // start and analyser data all return null so AudioMeter falls into
    // synthetic mode without flicker.
    if (!analyserRef.current) return null;
    return levelRef.current;
  }, []);

  // --- Browser mode (Web Speech API) ---

  // Returns false when recognition could not start. The session it belongs to
  // is captured here so its end can be settled even after restarts.
  //
  // Every callback and the delayed restart are fenced to this recognition
  // instance AND its session: once the instance is no longer the current one
  // (a stop, an error, an interruption, a restart, or a newer session), its
  // late events deliver no words, start no recognizer and change no state.
  // A browser can still fire result/end events after stop() or an error.
  const startBrowser = useCallback((boundSessionSeq?: number): boolean => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return false;
    const sessionSeq = boundSessionSeq ?? unsettledSessionSeq();
    const custodyId = sessionIdentityRef.current?.queueId ?? null;

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    // Normalize to the 2-letter code regardless of whether i18n returned
    // a short 'en' or a full BCP-47 'en-US' from LanguageDetector.
    const lang = i18n.language?.split('-')[0] ?? 'en';
    recognition.lang = SPEECH_RECOGNITION_LANG[lang] ?? 'en-US';

    const owns = (): boolean =>
      recognitionRef.current === recognition &&
      (sessionSeq === null || unsettledSessionSeq() === sessionSeq);

    recognition.onresult = (event: {
      resultIndex: number;
      results: SpeechRecognitionResultList;
    }) => {
      if (!owns()) return;
      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        if (result.isFinal) {
          finalText += transcript;
        } else {
          interim += transcript;
        }
      }
      const trimmedFinal = finalText.trim();
      if (trimmedFinal) onFinalRef.current(trimmedFinal);
      onInterimRef.current?.(interim.trim());
    };

    // A session that ends without a stop (an error, a failed restart) is
    // settled here, unless a stop already owns its end.
    const endSessionWithoutStop = () => {
      if (sessionSeq !== null && !stopUnderwayRef.current) {
        if (recognitionRef.current === recognition) recognitionRef.current = null;
        // Its words were delivered as they arrived: nothing to keep.
        if (custodyId) endCaptureWithoutPayload(custodyId);
        settleSession(sessionSeq, { kind: 'ended' });
      }
    };

    recognition.onerror = (event: { error: string }) => {
      if (!owns()) return;
      if (event.error === 'aborted' || event.error === 'no-speech') return;
      setError(`Speech recognition error: ${event.error}`);
      shouldBeListening.current = false;
      setIsListening(false);
      onInterimRef.current?.('');
      endSessionWithoutStop();
    };

    recognition.onend = () => {
      if (!owns()) return;
      if (shouldBeListening.current) {
        setTimeout(() => {
          // Still this instance's session and still wanted: restart it.
          if (owns() && shouldBeListening.current) {
            let restarted = false;
            try {
              restarted = startBrowser(sessionSeq ?? undefined);
            } catch {
              restarted = false;
            }
            if (!restarted) {
              shouldBeListening.current = false;
              setIsListening(false);
              onInterimRef.current?.('');
              endSessionWithoutStop();
            }
          }
        }, 80);
      } else {
        setIsListening(false);
        onInterimRef.current?.('');
        endSessionWithoutStop();
      }
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setIsListening(true);
      setError(null);
      // Gated pulse — only fires on the initial start of a session.
      // recognition.onend auto-restarts startBrowser internally; we
      // don't want a fresh "we're listening" pulse on every restart.
      if (!sessionStartedRef.current) {
        sessionStartedRef.current = true;
        pulseStart();
      }
      return true;
    } catch (err) {
      console.error('[Adia] speech start failed', err);
      return false;
    }
  }, [settleSession, unsettledSessionSeq]);

  const stopBrowser = useCallback((): Promise<VoiceStopResult> => {
    // Stop pulse fires synchronously even though the recognition tear-
    // down is effectively instant — gives the user immediate feedback
    // at the moment they tap. Gated on sessionStartedRef so a no-op
    // stop (e.g. clearing text before the session ever started) doesn't
    // emit a phantom confirmation.
    if (sessionStartedRef.current) {
      sessionStartedRef.current = false;
      pulseStop();
    }
    shouldBeListening.current = false;
    startingRef.current = false;
    cancelRequestedRef.current = false;
    const custodyId = sessionIdentityRef.current?.queueId ?? null;
    const r = recognitionRef.current;
    if (r) {
      try {
        r.stop();
      } catch {
        // ignore
      }
      recognitionRef.current = null;
    }
    setIsListening(false);
    onInterimRef.current?.('');
    if (custodyId) {
      const claim = claimCaptureStop(custodyId, 'stop');
      // A normal browser Stop delivered its words as they arrived. Another
      // claimant's end (an interruption keeping the words) is joined instead.
      if (claim === 'joined') return awaitCaptureStopResult(custodyId);
      endCaptureWithoutPayload(custodyId);
    }
    return Promise.resolve({ durability: 'not-required' });
  }, []);

  // --- Enhanced mode (MediaRecorder → upstream STT) ---

  const startEnhanced = useCallback(async (
    acceptedSource: 'prayer' | 'bookmark',
    acceptedIdentity: VoiceSessionIdentity,
    acceptedDestination?: AudioRecoveryDestination,
    acceptedScriptureAnchor?: ScriptureAnchor,
    acceptedCaptureToken?: string,
    acceptedSessionSeq?: number,
  ) => {
    // A start that ends before recording settles its session here, so the
    // capture admission (R4) never waits on a session that cannot stop.
    const custodyId = acceptedIdentity.queueId;
    const failStart = () => {
      // Nothing was recorded: the custody record ends empty.
      endCaptureWithoutPayload(custodyId);
      if (acceptedSessionSeq !== undefined) {
        settleSession(acceptedSessionSeq, { kind: 'start-failed' });
      }
    };
    const fallBackToBrowser = () => {
      sessionModeRef.current = 'browser';
      if (!startBrowser()) {
        shouldBeListening.current = false;
        sessionModeRef.current = null;
        failStart();
        explainStartUnavailable();
        return;
      }
      markCaptureRecording(custodyId, 'browser');
    };
    // try/finally guarantees startingRef is cleared on EVERY exit path —
    // including thrown errors from new MediaRecorder() on engines that
    // reject our mimeType. Without this, a constructor throw would leave
    // startingRef stuck true and permanently block future mic taps.
    try {
      if (!hasMediaRecorder() || !navigator.mediaDevices?.getUserMedia) {
        // Should have been caught by isSupported; fall back to browser mode.
        fallBackToBrowser();
        return;
      }

      // V.A.2: Storage upload requires an authenticated session. If the
      // user is signed out (anonymous), enhanced mode is structurally
      // unavailable — RLS would reject the upload and the v2 endpoint
      // would 401. Set fallbackReason='unauthenticated' so the listening
      // pill labels the fallback (matches the offline / service voice
      // labels) and route this tap to browser STT. Subsequent taps go
      // straight to browser via getEffectiveEnhancedVoice() which reads
      // fallbackReason; the auto-clear in sync.ts wipes the flag the
      // moment a session lands.
      const sb = getSupabase();
      const session = sb ? (await sb.auth.getSession()).data.session : null;
      if (!session?.user?.id) {
        useSettingsStore.getState().setFallbackReason('unauthenticated');
        if (!shouldBeListening.current) {
          endCaptureWithoutPayload(custodyId);
          return;
        }
        fallBackToBrowser();
        return;
      }
      const recordingOwnerId = session.user.id;
      if (
        acceptedIdentity.ownerId !== recordingOwnerId ||
        getAuthUserId() !== recordingOwnerId ||
        getAuthEpoch() !== acceptedIdentity.authEpoch ||
        await getContentOwnerBinding() !== recordingOwnerId
      ) {
        shouldBeListening.current = false;
        sessionModeRef.current = null;
        failStart();
        return;
      }

      // Optimistic: show listening state BEFORE awaiting getUserMedia.
      // Without this, the mic button stays idle during the first-load
      // permission prompt and users tap it again thinking nothing happened
      // — which used to spawn a second concurrent startEnhanced that
      // clobbered the first recorder's chunks array and produced silent
      // failures. Rolled back on denial.
      setIsListening(true);
      setError(null);

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.error('[Adia] microphone permission denied', err);
        setError('Microphone permission denied');
        shouldBeListening.current = false;
        setIsListening(false);
        failStart();
        return;
      }

      // If the user hit stop before we even got the stream, bail cleanly.
      // That stop already settled this session. A capture retired while the
      // microphone started (its provider was torn down, or the owner changed)
      // creates no recorder either: its tracks stop and nothing is kept.
      if (!shouldBeListening.current || !captureMayStartRecording(custodyId)) {
        stream.getTracks().forEach((t) => t.stop());
        setIsListening(false);
        endCaptureWithoutPayload(custodyId);
        return;
      }

      mediaStreamRef.current = stream;

      // Pick an explicitly-supported mimeType so the container we record
      // matches the filename extension the server sends upstream. Falling
      // back to the browser default (which varies by engine) was the root
      // of Safari STT returning empty text while Chrome worked.
      const mimeType = pickAudioMimeType();
      let recorder: MediaRecorder;
      try {
        recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
      } catch (err) {
        console.error('[Adia] MediaRecorder constructor failed', err);
        setError('Recorder unavailable');
        stream.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        shouldBeListening.current = false;
        setIsListening(false);
        failStart();
        return;
      }

      // Per-recorder chunks array, attached to the recorder so stopEnhanced's
      // onstop reads THIS recorder's data even if a second session has
      // started in parallel and reassigned the shared audioChunksRef.
      const chunks: Blob[] = [];
      (recorder as RecognitionInstance).__chunks = chunks;
      // Bookmarks Phase 2: bind the accepted source to THIS recorder
      // instance (same per-recorder pattern as __chunks, rule #14). The
      // onstop handler reads it back, immune to a concurrent start
      // overwriting any shared state across the getUserMedia await.
      (recorder as RecognitionInstance).__inlineSaveSource = acceptedSource;
      (recorder as RecognitionInstance).__recoveryDestination = acceptedDestination;
      (recorder as RecognitionInstance).__scriptureAnchor = acceptedScriptureAnchor;
      (recorder as RecognitionInstance).__ownerId = recordingOwnerId;
      (recorder as RecognitionInstance).__authEpoch = acceptedIdentity.authEpoch;
      (recorder as RecognitionInstance).__queueId = acceptedIdentity.queueId;
      (recorder as RecognitionInstance).__destinationId = acceptedIdentity.destinationId;
      (recorder as RecognitionInstance).__sourceDeviceId = acceptedIdentity.sourceDeviceId;
      (recorder as RecognitionInstance).__captureToken = acceptedCaptureToken;
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      // The recorder's stop event is bound ONCE, here, and goes to custody,
      // which advances only the record that expects it (section 3.5). No
      // claimant (Stop, interruption, teardown, Cancel) ever rebinds it.
      recorder.onstop = () => {
        const mime = recorder.mimeType || mimeType || 'audio/webm';
        const blob = chunks.length > 0 ? new Blob(chunks, { type: mime }) : null;
        deliverRecorderStop(custodyId, blob, mime);
      };
      audioChunksRef.current = chunks;
      mediaRecorderRef.current = recorder;

      try {
        // No timeslice. MediaRecorder emits ONE Blob on stop() with the
        // full single-segment container. Multi-chunk concatenation (from
        // timeslice > 0) produces Chrome's "unknown duration" WebM that
        // xAI's STT occasionally rejects with 502 — especially on the
        // second back-to-back recording. The original "first two taps
        // silent" race was a concurrent-sessions bug, fixed by the
        // startingRef single-flight guard, not by timeslice.
        recorder.start();
        markCaptureRecording(custodyId, 'enhanced');
        setError(null);

        // V.A.3: arm the silent 10-minute cap timer. Bound to THIS
        // recorder instance (not a shared ref) so a hypothetical second
        // concurrent session can't stomp it — same per-recorder pattern
        // as __chunks per rule #14. The callback marks the recorder as
        // auto-stopped and routes through stopListening() (via
        // stopListeningRef since stopListening is declared later in
        // this hook) so the public stopPromiseRef guard handles any
        // user-initiated stop racing it. Cleared in stopEnhanced /
        // finalizeForInterruption / unmount cleanup so it never fires
        // after the recorder retires.
        const timer = setTimeout(() => {
          (recorder as RecognitionInstance).__autoStopped = true;
          void stopListeningRef.current?.();
        }, AUTO_STOP_MS);
        (recorder as RecognitionInstance).__autoStopTimer = timer;

        // Wire the analyser tap on the SAME stream the recorder reads
        // from. Both are independent consumers of the MediaStream.
        // Placement (after recorder.start()) means there is no error
        // path where AudioContext exists but recorder.start() hasn't
        // been called — R4 in the B.1 ratification is structurally
        // satisfied. Do not move this above recorder.start() without
        // re-deriving the cleanup paths.
        setupAudioMeter(stream);

        // Gated pulse — fires once per session even if some future
        // path re-enters startEnhanced internally.
        if (!sessionStartedRef.current) {
          sessionStartedRef.current = true;
          pulseStart();
        }
      } catch (err) {
        console.error('[Adia] recorder start failed', err);
        setError('Could not start recording');
        stream.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        shouldBeListening.current = false;
        setIsListening(false);
        failStart();
        // No AudioContext yet (setupAudioMeter only runs in the success
        // branch above), so nothing to tear down on this failure path.
      }
    } finally {
      startingRef.current = false;
    }
  }, [startBrowser, setupAudioMeter, settleSession]);

  const stopEnhanced = useCallback((): Promise<VoiceStopResult> => {
    const timing = createVoiceTiming('stop');
    timing?.start('stop_to_delivery');
    // Stop pulse fires synchronously even though tear-down is async —
    // the user wants instant feedback at the moment they tap stop.
    // Gated on sessionStartedRef so no-op stops (mid-setup races,
    // cancel-before-start) don't emit a phantom confirmation.
    if (sessionStartedRef.current) {
      sessionStartedRef.current = false;
      pulseStop();
    }
    shouldBeListening.current = false;
    const recorder = mediaRecorderRef.current;
    const stream = mediaStreamRef.current;
    const custodyId = sessionIdentityRef.current?.queueId ?? null;
    const claimant = cancelRequestedRef.current ? 'cancel' : 'stop';
    cancelRequestedRef.current = false;

    // V.A.3: clear the auto-stop timer bound to this recorder before
    // any further teardown. clearTimeout removes the queued task from
    // the JS event loop, so even if the timer was about to fire it
    // can no longer race the manual stop. Idempotent — safe if the
    // timer was never armed (start error path) or already cleared.
    clearAutoStopTimer(recorder);

    // One claimant per capture (account-change recording plan rev 4.1,
    // section 3.5). An interruption or teardown already ending this capture
    // is joined, never duplicated; its result is this Stop's result.
    const claim = custodyId ? claimCaptureStop(custodyId, claimant) : 'none';
    if (claim === 'joined' && custodyId) {
      timing?.finish();
      return awaitCaptureStopResult(custodyId);
    }

    if (!recorder || claim !== 'claimed' || !custodyId) {
      setIsListening(false);
      // Release the stream if one was acquired but recording never started.
      stream?.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
      // Tear down the analyser even if there's no recorder — the meter
      // can outlive a half-set-up session if startEnhanced raced ahead
      // of stopEnhanced.
      teardownAudioMeter();
      timing?.finish();
      return Promise.resolve({ durability: 'not-required' });
    }

    return new Promise<VoiceStopResult>((resolve) => {
      const cleanup = () => {
        teardownAudioMeter();
        if (mediaStreamRef.current === stream) mediaStreamRef.current = null;
        stream?.getTracks().forEach((t) => t.stop());
        if (mediaRecorderRef.current === recorder) mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        setIsListening(false);
        setIsTranscribing(false);
      };

      const finishStop = async (): Promise<VoiceStopResult> => {
        // The capture's own stop event (bound once at creation) decides the
        // payload; an overdue event leaves the capture `unresolved` and this
        // Stop waits for it rather than losing anything.
        timing?.start('finalize');
        const delivered = await awaitCapturePayload(custodyId);
        timing?.end('finalize');
        // Gone without ending empty or cancelled: read its real end.
        if (delivered.kind === 'ended') return awaitCaptureStopResult(custodyId);
        if (delivered.kind !== 'payload' || !(delivered.payload instanceof Blob)) {
          // Empty, or this was the explicit Cancel: the only discard.
          return { durability: 'not-required' };
        }
        const blob = delivered.payload;

        // Bookmarks Phase 2: the source bound to THIS recorder at start.
        // Read from the recorder (not a shared ref) so an interleaved
        // session can't retag this recording's orphan.
        const recordingSource =
          ((recorder as RecognitionInstance).__inlineSaveSource as
            | 'prayer'
            | 'bookmark'
            | undefined) ?? 'prayer';
        const recordingOwnerId =
          (recorder as RecognitionInstance).__ownerId as string | undefined;
        const recordingAuthEpoch =
          (recorder as RecognitionInstance).__authEpoch as number | undefined;
        const queueId =
          (recorder as RecognitionInstance).__queueId as string | undefined;
        const destinationId =
          (recorder as RecognitionInstance).__destinationId as string | undefined;
        const sourceDeviceId =
          (recorder as RecognitionInstance).__sourceDeviceId as string | undefined;
        const recoveryDestination =
          (recorder as RecognitionInstance).__recoveryDestination as
            | AudioRecoveryDestination
            | undefined;

        // Recording is done but text hasn't arrived yet. Flip into the
        // "writing" state so the UI can swap its pill label and the user
        // sees that something is happening.
        setIsListening(false);
        setIsTranscribing(true);

        const ext = extFromMimeType(delivered.mimeType);

        // Durability boundary: the owner-bound Blob row commits before any
        // upload or transcription begins, through the capture's custody
        // (db.commitCaptureRow: create if absent, or verify the identical
        // capture). A page kill from this point onward leaves a recoverable
        // queue row, never memory-only audio or an unregistered Storage object.
        let durabilityResult: VoiceStopResult = {
          durability: 'failed',
          error: new Error('voice-durable-capture-not-attempted'),
        };
        try {
          if (
            !recordingOwnerId ||
            typeof recordingAuthEpoch !== 'number' ||
            !Number.isSafeInteger(recordingAuthEpoch) ||
            !queueId ||
            queueId !== custodyId ||
            !destinationId ||
            !sourceDeviceId
          ) {
            throw new Error('voice-recording-identity-missing');
          }
          timing?.start('lock_wait');
          await runInlineAudioExclusive(queueId, recordingOwnerId, async () => {
            timing?.end('lock_wait');
            timing?.start('capture_commit');
            const commit = await commitStoppedEnhancedCaptureBeforeNetwork(
              async () => {
                const outcome = await commitCapture(queueId, 'stop', {
                  expectedOwnerId: recordingOwnerId,
                  inlineSaveSource: recordingSource,
                  deferProcessing: false,
                  continuePipeline: true,
                  publishToList: true,
                });
                if (outcome.kind === 'failed') throw outcome.error;
                if (outcome.kind !== 'durable') throw new Error('voice-durable-capture-not-ready');
              },
              () => voiceOwnerStillCurrent(recordingOwnerId, recordingAuthEpoch),
            );
            timing?.end('capture_commit');
            if (commit.status === 'failed') {
              durabilityResult = { durability: 'failed', error: commit.error };
              return;
            }
            durabilityResult = { durability: 'committed' };
            // Retirement (a provider teardown, or an owner or epoch change
            // seen after an await) forbids every further effect. Each step
            // asks custody's `captureMayContinue`, and the queue helpers ask
            // it again inside their own awaits and writes: no C2
            // notice, no upload, registry call, transcription, transcript
            // write or words. A step already in flight returns, applies
            // nothing, and finishes the pipeline.
            try {
              if (!commit.ownerStillCurrent) retireCapture(queueId);
              if (!captureMayContinue(queueId)) return;
              // C2 (section 8.5): announce durability from the recorder's own
              // bindings. Never awaited, guarded, and run in a microtask, so it
              // cannot add an await to, or throw into, this pipeline.
              const notifyDurable = onCaptureDurableRef.current;
              if (notifyDurable) {
                const notice: CaptureDurableNotice = {
                  queueId,
                  ownerId: recordingOwnerId,
                  authEpoch: recordingAuthEpoch,
                  recoveryDestination: recoveryDestination
                    ? { ...recoveryDestination }
                    : undefined,
                  captureToken:
                    (recorder as RecognitionInstance).__captureToken as string | undefined,
                };
                queueMicrotask(() => {
                  try {
                    if (captureMayContinue(queueId)) notifyDurable(notice);
                  } catch {
                    // Observer only.
                  }
                });
              }
              timing?.start('upload');
              const storagePath = await uploadAudioToStorage(
                blob,
                queueId,
                recordingOwnerId,
                ext,
              );
              if (!captureMayContinue(queueId)) return;
              timing?.end('upload');
              timing?.start('pointer_commit');
              await persistUploadedAudioPointer(queueId, storagePath, recordingOwnerId);
              timing?.end('pointer_commit');
              if (!(await voiceOwnerStillCurrent(recordingOwnerId, recordingAuthEpoch))) {
                retireCapture(queueId);
                throw new Error('voice-owner-changed');
              }
              if (!captureMayContinue(queueId)) return;

              timing?.start('transcribe');
              const text = (
                await transcribeRegisteredQueueAudio(queueId, recordingOwnerId)
              ).trim();
              timing?.end('transcribe');
              if (!captureMayContinue(queueId)) return;
              if (!text) {
                setError('No speech detected');
              } else {
                // The queue row is the durable destination inbox. React state is
                // notified only after the transcript survives reload.
                timing?.start('transcript_commit');
                await persistInlineTranscription(queueId, text, recordingOwnerId);
                timing?.end('transcript_commit');
                if (!(await voiceOwnerStillCurrent(recordingOwnerId, recordingAuthEpoch))) {
                  retireCapture(queueId);
                  throw new Error('voice-owner-changed');
                }
                if (!captureMayContinue(queueId)) return;
                timing?.start('delivery');
                onFinalRef.current(text, queueId);
                timing?.end('delivery');
                timing?.end('stop_to_delivery');
                observeVoicePaintOpportunity();
                if ((recorder as RecognitionInstance).__autoStopped) {
                  dispatchVoiceAutoStop();
                }
              }
            } catch (err) {
              // A refused continuation (retired, or its owner or epoch
              // changed) is not a service failure and changes nothing else.
              if (
                (err as Error).message !== CAPTURE_RETIRED_ERROR &&
                captureMayContinue(queueId)
              ) {
                console.error('[voice-capture] inline-pipeline-failed', (err as Error).name);
                // Rule #21: only a temporary transcription-service failure (a
                // non-terminal, non-conflict classifier outcome) sends the next
                // NEW recording to browser speech for a minute. A terminal
                // reply (no speech, too large), a registry or content
                // conflict, an owner change, a local storage failure or a
                // retired capture leaves Enhanced Voice as it was. The durable
                // row and its recovery are unchanged either way.
                if (isTemporaryTranscriptionServiceFailure(err)) {
                  useSettingsStore.getState().setFallbackReason('service');
                }
              }
            }
          });
        } catch (err) {
          durabilityResult = {
            durability: 'failed',
            error: err instanceof Error ? err : new Error('voice-durable-capture-failed'),
          };
          // A failure before the commit was attempted keeps the bytes as a
          // failed capture (retry and warning), never a silent wait.
          markCaptureFailed(custodyId, durabilityResult.error);
        } finally {
          // After the exclusive section, so its exit can still ask custody
          // whether this capture may continue (a no-op unless active).
          finishCapturePipeline(custodyId);
        }

        if (durabilityResult.durability === 'failed') {
          console.error('[voice-capture] durable-capture-failed', durabilityResult.error.name);
          setError('Could not save recording');
        }
        return durabilityResult;
      };

      void finishStop()
        .catch((error: unknown): VoiceStopResult => ({
          durability: 'failed',
          error: error instanceof Error ? error : new Error('voice-durable-capture-failed'),
        }))
        .then((result) => {
          cleanup();
          timing?.finish();
          resolve(result);
        });

      try {
        recorder.stop();
      } catch {
        // The recorder refused to stop (it already ended). Its bytes are still
        // this recorder's own chunks: hand them to custody directly.
        const chunks =
          ((recorder as RecognitionInstance).__chunks as Blob[] | undefined) ?? [];
        const mime = recorder.mimeType || 'audio/webm';
        deliverRecorderStop(
          custodyId,
          chunks.length > 0 ? new Blob(chunks, { type: mime }) : null,
          mime,
        );
      }
    });
  }, [teardownAudioMeter]);

  // --- Public API ---

  // R4 backstop, read from refs only: a start in progress, a live capture, a
  // stop still settling (its pipeline may still be uploading or transcribing),
  // an interruption finalization, or any accepted session not yet settled.
  // A stale `shouldBeListening` with no live recorder or recognition and no
  // unsettled session is the self-healing case below, not a live capture.
  const isCaptureBusy = useCallback((): boolean => (
    startingRef.current ||
    (shouldBeListening.current &&
      (mediaRecorderRef.current !== null || recognitionRef.current !== null)) ||
    stopPromiseRef.current !== null ||
    interruptionInFlightRef.current ||
    unsettledSessionSeq() !== null
  ), [unsettledSessionSeq]);

  const getCaptureActivity = useCallback((): SpeechCaptureActivity => {
    const stopping =
      stopUnderwayRef.current ||
      stopPromiseRef.current !== null ||
      interruptionInFlightRef.current;
    return {
      busy: isCaptureBusy(),
      listening: unsettledSessionSeq() !== null && shouldBeListening.current && !stopping,
      stopping,
    };
  }, [isCaptureBusy, unsettledSessionSeq]);

  const startListening = useCallback((opts?: SpeechStartOptions): number | null => {
    // Core-content cutover: no capture at all. Recording would produce
    // audio bytes the active read-only gate cannot durably enqueue (enqueueAudio
    // rejects), so the only honest posture is to never open the mic.
    // Returning BEFORE getUserMedia also means no permission prompt.
    if (isCoreReadOnly()) return null;
    if (!isSupported) return null;
    // Single-flight guard: block only while a start is literally IN
    // PROGRESS (the getUserMedia await + recorder setup window). Prevents
    // rapid double-taps from spawning a second concurrent startEnhanced
    // that clobbers mediaRecorderRef mid-capture. startingRef is
    // guaranteed to clear via startEnhanced's try/finally — it cannot get
    // stuck even if the constructor throws.
    if (startingRef.current) return null;
    // Self-healing: if a previous session left shouldBeListening stuck
    // true (e.g. an unmount cleanup that didn't fully unwind), the next
    // user tap should still work. We detect "stuck" by looking at the
    // observable state — if nothing is actively recording and no
    // transcription is in flight, the ref is stale and we reset it.
    if (
      shouldBeListening.current &&
      !mediaRecorderRef.current &&
      !recognitionRef.current &&
      stopPromiseRef.current === null &&
      unsettledSessionSeq() === null
    ) {
      shouldBeListening.current = false;
    }
    // R4: never start while an earlier capture is still settling. A refused
    // start changes nothing.
    if (isCaptureBusy()) return null;
    startingRef.current = true;
    shouldBeListening.current = true;
    cancelRequestedRef.current = false;
    // Reset the B.2 idempotency guard so the next session's visibility
    // handler can finalize. R3: each session gets a fresh flag; old
    // session's flag never carries over.
    hasInterruptionFinalizedRef.current = false;
    const acceptedIdentity: VoiceSessionIdentity = {
      queueId: crypto.randomUUID(),
      // Update/testimony captures are bound to their prayer from the first
      // durable byte. New-prayer/bookmark drafts receive a stable opaque
      // per-capture id until Save binds the exact content operation.
      destinationId: opts?.recoveryDestination?.prayerId ?? crypto.randomUUID(),
      sourceDeviceId: getAudioDeviceId(),
      authEpoch: getAuthEpoch(),
      ownerId: getAuthUserId(),
    };
    sessionIdentityRef.current = acceptedIdentity;
    // Bookmarks Phase 2: capture the accepted source as an immutable
    // local AFTER the start guards above have accepted this session. An
    // ignored concurrent start returns at those guards and never reaches
    // this line, so it cannot affect the accepted session's source. The
    // local is threaded into startEnhanced by value (never a shared ref
    // read across the getUserMedia await).
    const acceptedSource: 'prayer' | 'bookmark' =
      opts?.inlineSaveSource ?? 'prayer';
    const acceptedScriptureAnchor = opts?.scriptureAnchor
      ? cloneScriptureAnchor(opts.scriptureAnchor)
      : undefined;
    // Effective value requires the independent deployment opt-in and respects
    // the runtime fallback flag. A disabled or temporarily unavailable cloud
    // path routes to browser-native Web Speech for this tap.
    const useEnhanced = getEffectiveEnhancedVoice() && hasMediaRecorder();
    const ctor = getSpeechRecognitionCtor();
    const noTranscriptionPath = !useEnhanced && !ctor;

    if (
      noTranscriptionPath ||
      // Custody needs the owner that admits the capture (section 3.1).
      typeof acceptedIdentity.ownerId !== 'string' ||
      !Number.isSafeInteger(acceptedIdentity.authEpoch)
    ) {
      // Enhanced disabled/unavailable AND no SpeechRecognition — nothing to do.
      shouldBeListening.current = false;
      startingRef.current = false;
      sessionIdentityRef.current = null;
      // Refused, so nothing was admitted or recorded; the tap is explained.
      if (noTranscriptionPath) explainStartUnavailable();
      return null;
    }
    // The capture's custody record exists from this synchronous admission,
    // while the owner runtime's lease is held (plan rev 4.1, section 6.1).
    admitCapture({
      custodyId: acceptedIdentity.queueId,
      runtimeId: custodyRuntimeIdRef.current,
      ownerId: acceptedIdentity.ownerId,
      authEpoch: acceptedIdentity.authEpoch,
      mode: useEnhanced ? 'enhanced' : 'browser',
      destinationId: acceptedIdentity.destinationId,
      sourceDeviceId: acceptedIdentity.sourceDeviceId,
      language: captureLanguage(),
      recoveryDestination: opts?.recoveryDestination
        ? { ...opts.recoveryDestination }
        : undefined,
      inlineSaveSource: acceptedSource,
      scriptureAnchor: acceptedScriptureAnchor,
      captureToken: opts?.captureToken,
    });
    const sessionSeq = ++sessionSeqRef.current;
    activeSessionRef.current = { seq: sessionSeq, settled: false };
    stopUnderwayRef.current = false;

    if (useEnhanced) {
      sessionModeRef.current = 'enhanced';
      // startingRef cleared in startEnhanced's finally.
      void startEnhanced(
        acceptedSource,
        acceptedIdentity,
        opts?.recoveryDestination,
        acceptedScriptureAnchor,
        opts?.captureToken,
        sessionSeq,
      );
      return sessionSeq;
    }
    sessionModeRef.current = 'browser';
    const started = startBrowser();
    startingRef.current = false;
    if (!started) {
      // Nothing recorded and nothing was handed to the caller yet: report a
      // refusal so the caller changes nothing.
      shouldBeListening.current = false;
      sessionModeRef.current = null;
      revokeCaptureAdmission(acceptedIdentity.queueId);
      sessionIdentityRef.current = null;
      activeSessionRef.current = { seq: sessionSeq, settled: true };
      explainStartUnavailable();
      return null;
    }
    return sessionSeq;
  }, [isSupported, startBrowser, startEnhanced, isCaptureBusy, unsettledSessionSeq]);

  const stopListening = useCallback((): Promise<VoiceStopResult> => {
    // If a stop is already in-flight, piggy-back on the same promise so two
    // callers don't race the cleanup.
    if (stopPromiseRef.current) return stopPromiseRef.current;

    const sessionSeq = unsettledSessionSeq();
    if (sessionSeq !== null) markStopUnderway();
    const mode = sessionModeRef.current;
    const p = mode === 'enhanced' ? stopEnhanced() : stopBrowser();
    const finish = (result: VoiceStopResult): VoiceStopResult => {
      stopPromiseRef.current = null;
      sessionModeRef.current = null;
      sessionIdentityRef.current = null;
      if (sessionSeq !== null) settleSession(sessionSeq, { kind: 'stopped', result });
      return result;
    };
    stopPromiseRef.current = p.then(finish, (error: unknown) => finish({
      durability: 'failed',
      error: error instanceof Error ? error : new Error('voice-stop-failed'),
    }));
    return stopPromiseRef.current;
  }, [stopBrowser, stopEnhanced, markStopUnderway, settleSession, unsettledSessionSeq]);

  const cancelListening = useCallback((): Promise<VoiceStopResult> => {
    // Same tear-down as stop, but in enhanced mode the captured audio is
    // dropped instead of transcribed — used by "clear" paths where the user
    // has abandoned the recording and wouldn't expect text to reappear 1–3s
    // later. The stop this issues claims the capture as an explicit Cancel.
    // A stop already underway is never turned into a cancel (B1 path 2).
    if (stopPromiseRef.current) return stopPromiseRef.current;
    cancelRequestedRef.current = true;
    return stopListening();
  }, [stopListening]);

  // ---- Defensive lifecycle (B.2) ----

  const finalizeForInterruption = useCallback((): Promise<
    | {
        mode: 'enhanced';
        blob: Blob;
        mimeType: string;
        ownerId: string;
        authEpoch: number;
        queueId: string;
        destinationId: string;
        sourceDeviceId: string;
      }
    | {
        mode: 'browser';
        ownerId?: string;
        authEpoch?: number;
        queueId?: string;
        destinationId?: string;
        sourceDeviceId?: string;
      }
    | null
  > => {
    // R3 idempotency: first call wins, subsequent calls return null
    // cleanly. Visibility + pagehide can both fire for the same
    // interruption; the second one no-ops here.
    if (hasInterruptionFinalizedRef.current) return Promise.resolve(null);
    hasInterruptionFinalizedRef.current = true;

    // Suppress B.1's pulseStop on the upcoming stop calls — system-
    // triggered finalization is not a user action.
    sessionStartedRef.current = false;

    const mode = sessionModeRef.current;
    if (!mode) return Promise.resolve(null);
    const identity = sessionIdentityRef.current;

    if (mode === 'enhanced') {
      const recorder = mediaRecorderRef.current;
      // Race guard. If iOS already reclaimed the mic during background
      // (recorder.state transitioned to 'inactive'), or a Stop already claimed
      // this capture, there is nothing for this finalizer to claim: resolve
      // null and the caller joins that Stop (section 3.5).
      if (!recorder || recorder.state !== 'recording' || !identity) {
        return Promise.resolve(null);
      }
      const custodyId = identity.queueId;
      if (claimCaptureStop(custodyId, 'interruption') !== 'claimed') {
        return Promise.resolve(null);
      }
      const interruptedSeq = unsettledSessionSeq();
      interruptionInFlightRef.current = true;
      markStopUnderway();
      // V.A.3: clear the auto-stop timer for THIS recorder before the stop,
      // so it cannot fire mid-finalize.
      clearAutoStopTimer(recorder);
      const stream = mediaStreamRef.current;

      // The recorder's own stop event (bound once at creation) delivers the
      // bytes to custody; this finalizer waits for them. An overdue event
      // keeps the capture `unresolved`, never lost.
      const finished = awaitCapturePayload(custodyId).then((delivered) => {
        // Inline the same teardown stopEnhanced's cleanup runs.
        shouldBeListening.current = false;
        teardownAudioMeter();
        stream?.getTracks().forEach((t) => t.stop());
        if (mediaStreamRef.current === stream) mediaStreamRef.current = null;
        if (mediaRecorderRef.current === recorder) mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        setIsListening(false);
        setIsTranscribing(false);
        if (sessionIdentityRef.current?.queueId === custodyId) {
          sessionIdentityRef.current = null;
        }
        // The finalizer now owns the capture; its persistence is the
        // caller's (useVoiceCapture) to settle.
        interruptionInFlightRef.current = false;
        if (interruptedSeq !== null) {
          settleSession(interruptedSeq, { kind: 'interrupted' });
        } else {
          stopUnderwayRef.current = false;
          setIsStopping(false);
        }
        if (delivered.kind !== 'payload' || !(delivered.payload instanceof Blob)) return null;
        return {
          mode: 'enhanced' as const,
          blob: delivered.payload,
          mimeType: delivered.mimeType,
          ownerId: identity.ownerId as string,
          authEpoch: identity.authEpoch,
          queueId: custodyId,
          destinationId: identity.destinationId,
          sourceDeviceId: identity.sourceDeviceId,
        };
      });

      try {
        recorder.stop();
      } catch {
        // Narrow race: state was 'recording' at our check, rejected stop()
        // between then and the call. Its chunks are still this recorder's.
        const chunks =
          ((recorder as RecognitionInstance).__chunks as Blob[] | undefined) ?? [];
        const mime = recorder.mimeType || 'audio/webm';
        deliverRecorderStop(
          custodyId,
          chunks.length > 0 ? new Blob(chunks, { type: mime }) : null,
          mime,
        );
      }
      return finished;
    }

    // Browser mode: stop recognition silently (no pulse). The interim
    // text persistence is the caller's job — it owns the interim string
    // (useVoiceCapture.handleInterim populates a state on the provider,
    // not down here). The capture is claimed for the interruption; the caller
    // delivers its words to custody.
    if (mode === 'browser') {
      const interruptedSeq = unsettledSessionSeq();
      if (identity) claimCaptureStop(identity.queueId, 'interruption');
      shouldBeListening.current = false;
      const r = recognitionRef.current;
      if (r) {
        try {
          r.stop();
        } catch {
          // ignore
        }
        recognitionRef.current = null;
      }
      // teardownAudioMeter is a no-op in browser mode (no analyser was
      // ever set up) but the call is safe to leave for symmetry.
      teardownAudioMeter();
      setIsListening(false);
      onInterimRef.current?.('');
      sessionIdentityRef.current = null;
      if (interruptedSeq !== null) settleSession(interruptedSeq, { kind: 'interrupted' });
      return Promise.resolve({
        mode: 'browser',
        ownerId: typeof identity?.ownerId === 'string' ? identity.ownerId : undefined,
        authEpoch: identity?.authEpoch,
        queueId: identity?.queueId,
        destinationId: identity?.destinationId,
        sourceDeviceId: identity?.sourceDeviceId,
      });
    }

    return Promise.resolve(null);
  }, [teardownAudioMeter, markStopUnderway, settleSession, unsettledSessionSeq]);

  useEffect(() => {
    return () => {
      shouldBeListening.current = false;
      startingRef.current = false;
      sessionStartedRef.current = false;
      // R3: clear B.2 interruption refs. The visibility handler re-binds on
      // next mount; this one is gone.
      hasInterruptionFinalizedRef.current = false;
      // Provider teardown is a claimant (plan rev 4.1, sections 3.3 and 3.5):
      // it retires this capture and, if it is still recording, stops it so the
      // recorder's own stop event delivers the bytes to custody, which commits
      // them to their owner. A start still waiting on the microphone ends
      // empty when it returns. A capture another claimant already ends is
      // joined (retired), never dropped.
      const identity = sessionIdentityRef.current;
      const recorder = mediaRecorderRef.current;
      const stream = mediaStreamRef.current;
      const claim = identity ? claimCaptureStop(identity.queueId, 'teardown') : 'none';
      // Tear down the analyser tap before releasing the stream so the
      // AudioContext closes cleanly while the source is still alive.
      teardownAudioMeter();
      const r = recognitionRef.current;
      if (r) {
        try {
          r.stop();
        } catch {
          // ignore
        }
        recognitionRef.current = null;
      }
      // V.A.3: clear any in-flight auto-stop timer before tearing
      // down the recorder. Idempotent if the timer was never armed
      // (component unmounting before any recording started).
      clearAutoStopTimer(recorder);
      if (claim === 'claimed' && identity && recorder && recorder.state !== 'inactive') {
        const custodyId = identity.queueId;
        // Release the microphone only after the final data and stop events.
        void awaitCapturePayload(custodyId).finally(() => {
          stream?.getTracks().forEach((t) => t.stop());
        });
        try {
          recorder.stop();
        } catch {
          const chunks =
            ((recorder as RecognitionInstance).__chunks as Blob[] | undefined) ?? [];
          const mime = recorder.mimeType || 'audio/webm';
          deliverRecorderStop(
            custodyId,
            chunks.length > 0 ? new Blob(chunks, { type: mime }) : null,
            mime,
          );
        }
      } else if (stream && (claim !== 'joined' || !recorder)) {
        stream.getTracks().forEach((t) => t.stop());
      }
      if (mediaStreamRef.current === stream && claim !== 'joined') mediaStreamRef.current = null;
      mediaRecorderRef.current = null;
    };
  }, [teardownAudioMeter]);

  // V.A.3: keep the bridge ref pointing at the latest stopListening so
  // the auto-stop setTimeout callback in startEnhanced can invoke it
  // without a forward TS reference. stopListening is stable (its deps
  // are themselves stable across renders), but assigning via effect
  // keeps the contract honest if that ever changes.
  useEffect(() => {
    stopListeningRef.current = stopListening;
  }, [stopListening]);

  const clearError = useCallback(() => setError(null), []);

  return {
    isListening,
    isTranscribing,
    isSupported,
    isStopping,
    error,
    clearError,
    startListening,
    getCaptureActivity,
    stopListening,
    cancelListening,
    getLevel,
    finalizeForInterruption,
  };
}
